import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, App, Button, Grid, Select, Tooltip } from 'antd';

import ConfirmWordModal from '../common/ConfirmWordModal';
import ContextBar from '../common/ContextBar';
import EmptyState from '../common/EmptyState';
import RecordBadge from '../common/RecordBadge';
import { CardOptionLabel, buildMemberCardOptions } from '../cards/CardVisuals';
import { DataState, Skeleton, deriveDataState } from '../loading';
import { ARROW_RIGHT } from '../icons';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { collectionCrumbs } from '../../utils/breadcrumbs';
import { cardPagePath } from '../../utils/cards';
import { collectionCurrencyCode } from '../../utils/collectionCurrency';
import { getCollectionMemberCardIds } from '../../utils/collections';
import { normalizeId } from '../../utils/ids';
import { isBorrowedCard } from '../../utils/linkedRecords';
import { canWriteCard } from '../../utils/permissions';
import { optionSearchFilter } from '../Input';
import AttestationModal from '../users/AttestationModal';
import { isElementValueEmpty } from '../../utils/elements';
import { validateElementValue } from '../../utils/elementValidation';
import MappingBoard from './MappingBoard';
import LeftBehindPanel from './LeftBehindPanel';
import useChangeCardDryRun from './useChangeCardDryRun';
import useChangeCardApply from './useChangeCardApply';
import ReviewStep, { confirmState } from './ReviewStep';
import ChangeCardResult from './ChangeCardResult';
import {
	BACK_TO_MAPPING,
	CHANGE_CARD,
	LOSE_CONFIRM_TITLE,
	LOSE_WORD,
	REVIEW_LABEL,
	REVIEW_NEEDS_FIXES,
	REVIEW_NEEDS_TARGET,
	LOSS_CODES,
	hasLoss,
	refusalText,
	warningText,
} from './reviewCopy';
import {
	acceptSuggestions,
	activeMapping,
	assign,
	buildValue,
	clear,
	composeProblems as composeProblemsOf,
	composeText,
	createReaches,
	dryRunBody,
	elementLabel,
	plainSourceName,
	problemsByTarget,
	selectionFromSearch,
	signingCardId,
	sourcesOf,
	suggestionsFor,
	summaryLine,
	targetsOf,
	toElementEntry,
	updateMapping,
	verdictFor,
} from './changeCardModel';

const EMPTY = Object.freeze({});

const hasOwnValue = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

/**
 * CHANGE CARD — the mapping utility (KPE-028, KPR-707).
 *
 * `/collections/:collectionId/change-card?card=<source card>&ids=a,b` (or
 * `&q=<list filter>`, or `&all=1` for every item of the card). The person
 * picks the card the items should be on, then fills each of its elements from
 * one of the source card's: by dragging a source pill onto a target row, or
 * with the row's Choose picker (the keyboard and phone path). Every change
 * re-runs the dry run (debounced), so each row's status and counts are the
 * server's. The entry points that open this page are KPR-710.
 *
 * Then the review step (KPR-709, ReviewStep): the same dry run in words, the
 * values a required element still needs, the rules to run, and one sentence
 * the Change card button confirms — through a typed "lose" when any value
 * will be kept in History only. The move (KPR-702) answers with its result,
 * or (KPR-705) a background task, shown by ChangeCardResult with Undo.
 */
export default function ChangeCardPage() {
	const { collectionId } = useParams();
	const [params] = useSearchParams();
	const navigate = useNavigate();
	const { modal } = App.useApp();
	const screens = Grid.useBreakpoint();
	const isPhone = screens.md === false;
	const { collection, collectionLoading } = useCollectionContext() || {};
	const { cards, cardsById, getElementsForCard, loading: cardsLoading } = useCardDefinitions();

	const selection = useMemo(() => selectionFromSearch(params), [params]);
	const sourceCard = selection ? cardsById?.[selection.card] : null;
	const [targetId, setTargetId] = useState(null);
	const [mapping, setMapping] = useState(EMPTY);
	const [leftBehind, setLeftBehind] = useState(EMPTY);
	const [step, setStep] = useState('map');
	const [asks, setAsks] = useState(EMPTY);
	const [ruleTicks, setRuleTicks] = useState(EMPTY);
	const [wordOpen, setWordOpen] = useState(false);

	const { isDirty, guardDiscard } = useDirtyForm({ targetId, mapping, leftBehind, asks, ruleTicks }, { enabled: step !== 'done' });

	const ctx = useMemo(() => ({ reaches: createReaches(cardsById) }), [cardsById]);
	const opts = useMemo(() => ({ collectionCurrency: collectionCurrencyCode(collection) }), [collection]);

	const sources = useMemo(() => (sourceCard ? sourcesOf(getElementsForCard(selection.card)) : []), [sourceCard, selection, getElementsForCard]);
	const { offered: targets, withheld } = useMemo(
		() => (targetId ? targetsOf(getElementsForCard(targetId)) : { offered: [], withheld: [] }),
		[targetId, getElementsForCard]
	);
	const sourcesByName = useMemo(() => Object.fromEntries(sources.map(el => [el.name, el])), [sources]);
	const targetsByName = useMemo(() => Object.fromEntries(targets.map(el => [el.name, el])), [targets]);
	const targetCard = targetId ? cardsById?.[targetId] : null;
	// A value built in the composer that holds nothing yet fills nothing; the
	// dry run, the suggestions and the review do not see it (KPR-708).
	const live = useMemo(() => activeMapping(mapping), [mapping]);
	const sourceNames = useMemo(() => sources.map(el => el.name), [sources]);
	const composeProblems = useMemo(() => composeProblemsOf(mapping, targetsByName, sourceNames), [mapping, targetsByName, sourceNames]);
	const copy = useMemo(() => ({
		source: sourceCard?.name || null,
		target: targetCard?.name || null,
		labels: Object.fromEntries(sources.map(el => [el.name, elementLabel(el)])),
		targetLabels: Object.fromEntries(targets.map(el => [el.name, elementLabel(el)])),
	}), [sourceCard, targetCard, sources, targets]);

	// A value given for a required element nothing maps (an `ask` fill) is
	// sent once it is one the element accepts; until then the dry run keeps
	// naming the element as required.
	const askErrors = useMemo(() => {
		const out = {};
		Object.keys(asks).forEach(name => {
			const el = targetsByName[name];
			if (!el || isElementValueEmpty(el, asks[name])) return;
			const problem = validateElementValue(el, asks[name]);
			if (problem) out[name] = problem;
		});
		return out;
	}, [asks, targetsByName]);
	const sentAsks = useMemo(() => {
		const out = {};
		Object.keys(asks).forEach(name => {
			const el = targetsByName[name];
			if (!el || live[name] || isElementValueEmpty(el, asks[name]) || askErrors[name]) return;
			out[name] = asks[name];
		});
		return out;
	}, [asks, targetsByName, live, askErrors]);

	// The cards the items may move to: the collection's own cards (and those
	// it owns) that the person may write, never a card a link lends it, never
	// the source card itself. The dry run is still the judge.
	const targetOptions = useMemo(() => {
		if (!collection || !cards) return [];
		const members = getCollectionMemberCardIds(collection, cards);
		return buildMemberCardOptions(cards, members)
			.filter(opt => opt.value !== selection?.card)
			.filter(opt => !isBorrowedCard(opt.card))
			.filter(opt => canWriteCard(collection, opt.value))
			.map(opt => ({ ...opt, label: <CardOptionLabel card={opt.card} depth={opt.depth} /> }));
	}, [collection, cards, selection]);

	const answers = useMemo(() => {
		const out = {};
		Object.keys(mapping).forEach(name => {
			const source = sourcesByName[plainSourceName(mapping[name], sourceNames)];
			const target = targetsByName[name];
			if (source && target) out[name] = verdictFor(source, target, ctx);
		});
		return out;
	}, [mapping, sourcesByName, targetsByName, ctx, sourceNames]);

	const suggestions = useMemo(() => suggestionsFor(sources, targets, live, ctx), [sources, targets, live, ctx]);

	const body = useMemo(
		() => (sourceCard && targetId
			? dryRunBody(selection, targetId, live, leftBehind, sourceNames, { asks: sentAsks, rules: ruleTicks, targetsByName })
			: null),
		[sourceCard, selection, targetId, live, leftBehind, sourceNames, sentAsks, ruleTicks, targetsByName]
	);
	const dry = useChangeCardDryRun(collectionId, body);
	const problems = useMemo(() => problemsByTarget(dry.problems), [dry.problems]);
	const move = useChangeCardApply(collectionId, { signingCardId: targetId && selection ? signingCardId(cardsById, targetId, selection.card) : null });

	// The required elements the review asks a value for: those the dry run
	// names as required and unfilled, and those already given one.
	const giveElements = useMemo(() => {
		const names = new Set(Object.keys(asks));
		(dry.problems || []).forEach(p => { if (p?.code === 'required' && p.element) names.add(p.element); });
		return [...names].filter(name => targetsByName[name] && !live[name]).map(name => targetsByName[name]);
	}, [asks, dry.problems, targetsByName, live]);
	const missing = useMemo(
		() => giveElements.filter(el => !hasOwnValue(sentAsks, el.name)).map(el => elementLabel(el)),
		[giveElements, sentAsks]
	);

	const chooseTarget = useCallback(next => {
		const id = normalizeId(next);
		if (id === targetId) return;
		const apply = () => {
			setTargetId(id);
			setMapping(EMPTY);
			setLeftBehind(EMPTY);
			setAsks(EMPTY);
			setRuleTicks(EMPTY);
		};
		if (!Object.keys(mapping).length) {
			apply();
			return;
		}
		modal.confirm({
			title: 'Change the target card?',
			content: 'The elements you have mapped are cleared.',
			okText: 'Change card',
			cancelText: 'Keep this card',
			onOk: apply,
		});
	}, [targetId, mapping, modal]);

	const onAssign = useCallback((targetName, sourceName, how) => {
		setMapping(prev => assign(prev, targetName, sourcesByName[sourceName], targetsByName[targetName], ctx, how ? { ...opts, ...how } : opts));
	}, [sourcesByName, targetsByName, ctx, opts]);
	const onBuild = useCallback(targetName => setMapping(prev => buildValue(prev, targetName)), []);
	const onCompose = useCallback((targetName, text) => {
		setMapping(prev => composeText(prev, targetName, text, { sourcesByName, targetsByName, ctx, opts }));
	}, [sourcesByName, targetsByName, ctx, opts]);
	const onUsePicker = useCallback(targetName => {
		setMapping(prev => (prev[targetName] && !String(prev[targetName].text || '').trim() ? clear(prev, targetName) : toElementEntry(prev, targetName, sourceNames)));
	}, [sourceNames]);
	const onClear = useCallback(targetName => setMapping(prev => clear(prev, targetName)), []);
	const onUpdate = useCallback((targetName, patch) => setMapping(prev => updateMapping(prev, targetName, patch)), []);
	const onAcceptSuggestion = useCallback(targetName => {
		const one = suggestions.filter(s => s.target === targetName);
		setMapping(prev => acceptSuggestions(prev, one, sourcesByName, targetsByName, ctx, opts));
	}, [suggestions, sourcesByName, targetsByName, ctx, opts]);
	const acceptAll = useCallback(() => {
		setMapping(prev => acceptSuggestions(prev, suggestions, sourcesByName, targetsByName, ctx, opts));
	}, [suggestions, sourcesByName, targetsByName, ctx, opts]);
	const onLeftBehind = useCallback((name, choice) => setLeftBehind(prev => ({ ...prev, [name]: choice })), []);
	const onAsk = useCallback((name, value) => setAsks(prev => ({ ...prev, [name]: value })), []);
	const onTick = useCallback((id, ticked) => {
		if (!id) return;
		setRuleTicks(prev => ({ ...prev, [id]: ticked }));
	}, []);

	const confirm = confirmState({ dry, missing, applying: move.busy });
	const loses = step === 'review' && hasLoss(dry.data?.warnings);
	// A refusal whose answer is to review again (the rules or the cards
	// changed, the token expired) runs the dry run again for a new token.
	const { retry } = dry;
	const settle = useCallback(outcome => {
		if (outcome?.data) setStep('done');
		else if (outcome?.refusal && refusalText(outcome.refusal.code, outcome.refusal.message, {}).review) retry();
	}, [retry]);
	const doMove = useCallback(async () => {
		setWordOpen(false);
		if (!confirmState({ dry, missing, applying: move.busy }).ok) return;
		settle(await move.apply(body, dry.data.confirm.token));
	}, [dry, missing, move, body, settle]);
	const onConfirm = () => (loses ? setWordOpen(true) : doMove());
	const onSigned = useCallback(async signature => {
		settle(await move.sign(signature));
	}, [move, settle]);
	const refusal = move.refusal ? refusalText(move.refusal.code, move.refusal.message, copy) : null;
	const toReview = () => {
		move.clearRefusal();
		setStep('review');
	};
	const toMap = () => {
		move.clearRefusal();
		setStep('map');
	};

	const leave = () => guardDiscard(() => navigate(`/collections/${collectionId}/items`));

	const loading = collectionLoading || cardsLoading;
	const crumbs = collectionCrumbs(collection, collectionId, 'Change card');

	if (!loading && (!selection || !sourceCard)) {
		return (
			<div className="change-card">
				<ContextBar breadcrumb={crumbs} title="Change card" />
				<EmptyState
					title={selection ? 'That card is not here' : 'No items to change'}
					description={selection
						? 'The card these items are on is not one you can use in this collection. Open the items from the list and choose Change card there.'
						: 'Select the items to change in a list, then choose Change card.'}
					primaryAction={{ label: 'Go to the items', to: `/collections/${collectionId}/items` }}
				/>
			</div>
		);
	}

	const summary = dry.data ? summaryLine(dry.data.items) : null;
	const onlyRequired = dry.problems.length > 0 && dry.problems.every(p => p.code === 'required');
	const comments = (dry.data?.warnings || []).find(w => w.code === 'comments_off' && w.canEnableComments);
	// Review opens once there is something to review: counts, a block, or
	// only required elements to give a value for (the review asks them).
	const fixes = dry.problems.some(p => p.code !== 'required') || Object.keys(composeProblems).length > 0;
	const reviewReason = !targetId ? REVIEW_NEEDS_TARGET : (fixes ? REVIEW_NEEDS_FIXES : null);
	const canReview = !reviewReason && targets.length > 0 && !!(dry.data || onlyRequired || dry.error?.code === 'change_card_blocked_by_lookups');
	const tip = (title, button) => (title ? <Tooltip title={title}><span>{button}</span></Tooltip> : button);
	const actions = step === 'map' ? (
		<>
			<Button onClick={leave}>{isDirty ? 'Cancel' : 'Back'}</Button>
			{tip(reviewReason, <Button type="primary" disabled={!canReview} onClick={toReview}>{REVIEW_LABEL}</Button>)}
		</>
	) : step === 'review' ? (
		<>
			<Button onClick={toMap}>{BACK_TO_MAPPING}</Button>
			{tip(confirm.reason, <Button type="primary" disabled={!confirm.ok} loading={move.busy} onClick={onConfirm}>{CHANGE_CARD}</Button>)}
		</>
	) : null;
	const lossWarnings = (dry.data?.warnings || []).filter(w => LOSS_CODES.includes(w.code) && Number(w.items) > 0);

	return (
		<div className="change-card">
			<ContextBar
				breadcrumb={crumbs}
				title="Change card"
				actions={actions}
			/>
			<DataState
				state={deriveDataState({ loading, hasData: !!sourceCard })}
				hasData={!!sourceCard}
				skeleton={<Skeleton variant="list-row" count={6} />}
				label="change card"
			>
				{step === 'review' ? (
					<div className="change-card__body">
						<ReviewStep
							dry={dry}
							ctx={copy}
							cardsById={cardsById}
							sourcesByName={sourcesByName}
							targetsByName={targetsByName}
							giveElements={giveElements}
							asks={asks}
							askErrors={askErrors}
							onAsk={onAsk}
							onTick={onTick}
							onLeftBehind={onLeftBehind}
							refusal={refusal}
							missing={missing}
						/>
					</div>
				) : step === 'done' ? (
					<div className="change-card__body">
						<ChangeCardResult result={move.result} ctx={copy} collectionId={collectionId} />
					</div>
				) : (
				<div className="change-card__body">
					<section className="change-card__panel change-card__head" aria-label="Cards">
						<div className="change-card__route">
							{sourceCard ? <RecordBadge kind="card" record={sourceCard} title={sourceCard.name} size="sm" /> : null}
							<ARROW_RIGHT aria-hidden="true" className="change-card__arrow" />
							<Select
								className="change-card__target-picker"
								placeholder="Choose the card to move to"
								showSearch
								filterOption={optionSearchFilter}
								value={targetId || undefined}
								options={targetOptions}
								onChange={chooseTarget}
								aria-label="The card to move to"
								notFoundContent="No other card here can take these items."
							/>
						</div>
						<p className="change-card__summary" aria-live="polite">
							{!targetId
								? 'Choose the card these items should be on.'
								: onlyRequired
									? 'Fill the required elements to see what will move.'
									: summary || (dry.loading ? 'Working out what will move.' : null)}
						</p>
						{dry.error ? (
							<Alert
								type="error"
								showIcon
								message={dry.error.message}
								action={dry.error.code === 'query_timeout' || !dry.error.code ? <Button size="small" onClick={dry.retry}>Try again</Button> : null}
							/>
						) : null}
					</section>

					{targetId && suggestions.length ? (
						<Alert
							className="change-card__suggestions"
							type="info"
							showIcon
							message={suggestions.length === 1
								? '1 element matches by name.'
								: `${suggestions.length} elements match by name.`}
							action={<Button size="small" onClick={acceptAll}>Accept all</Button>}
						/>
					) : null}

					{targetId ? (
						targets.length ? (
							<div className={`change-card__mapping${dry.loading ? ' is-refreshing' : ''}`} aria-busy={dry.loading}>
								<MappingBoard
									sources={sources}
									targets={targets}
									withheld={withheld}
									mapping={mapping}
									answers={answers}
									ctx={ctx}
									dryRun={dry.data}
									problems={problems}
									suggestions={suggestions}
									isPhone={isPhone}
									getElementsForCard={getElementsForCard}
									composeProblems={composeProblems}
									stale={dry.loading || dry.problems.length > 0}
									onAssign={onAssign}
									onClear={onClear}
									onUpdate={onUpdate}
									onBuild={onBuild}
									onCompose={onCompose}
									onUsePicker={onUsePicker}
									onAcceptSuggestion={onAcceptSuggestion}
								/>
								<LeftBehindPanel
									unmapped={dry.data?.unmapped || []}
									warnings={dry.data?.warnings || []}
									onChange={onLeftBehind}
									ctx={copy}
								/>
								{comments ? (
									<p className="change-card__note">
										<Link to={cardPagePath(targetId, collectionId)}>Turn on comments for this card</Link> to keep what is left behind with each item.
									</p>
								) : null}
							</div>
						) : (
							<EmptyState
								title="Nothing on this card can be filled"
								description="Every element of this card is filled in by keepr or is one you cannot change. Choose another card."
							/>
						)
					) : null}
				</div>
				)}
			</DataState>
			<ConfirmWordModal
				open={wordOpen}
				word={LOSE_WORD}
				title={LOSE_CONFIRM_TITLE}
				okText={CHANGE_CARD}
				onOk={doMove}
				onCancel={() => setWordOpen(false)}
				busy={move.busy}
			>
				{lossWarnings.map(w => <p key={w.code}>{warningText(w, copy).text}</p>)}
			</ConfirmWordModal>
			<AttestationModal
				open={!!move.attestation}
				mode={move.attestation?.mode}
				collectionId={collectionId}
				cardId={move.attestation?.cardId}
				title={CHANGE_CARD}
				onClose={move.cancelSign}
				onSigned={onSigned}
			/>
		</div>
	);
}
